import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  useCreateProviderCredential,
  useUpdateProviderCredential,
} from '../services/providerCredentialsApi';
import type {
  ProviderCredential,
  ProviderCredentialCreateRequest,
  ProviderCredentialUpdateRequest,
  ProviderType,
  UsageType,
} from '../types/provider-credentials.types';

const PROVIDER_TYPES: ProviderType[] = ['stt', 'tts', 'llm'];
const USAGE_TYPES: UsageType[] = ['text', 'realtime'];

interface FormState {
  providerType: ProviderType;
  providerName: string;
  keyName: string;
  apiKey: string;
  usageType: UsageType;
  priority: string;
  maxConcurrentSessions: string;
  isActive: boolean;
}

const defaultForm: FormState = {
  providerType: 'stt',
  providerName: '',
  keyName: 'key_1',
  apiKey: '',
  usageType: 'text',
  priority: '1',
  maxConcurrentSessions: '',
  isActive: true,
};

interface CredentialFormDialogProps {
  mode: 'create' | 'edit';
  credential?: ProviderCredential;
  onClose: () => void;
  onSuccess: () => void;
}

export function CredentialFormDialog({
  mode,
  credential,
  onClose,
  onSuccess,
}: CredentialFormDialogProps) {
  const createMutation = useCreateProviderCredential();
  const updateMutation = useUpdateProviderCredential();

  const [form, setForm] = useState<FormState>(() => {
    if (mode === 'edit' && credential) {
      return {
        providerType: credential.providerType,
        providerName: credential.providerName,
        keyName: credential.keyName,
        apiKey: credential.credentials.api_key,
        usageType: credential.usageType,
        priority: credential.priority.toString(),
        maxConcurrentSessions: credential.maxConcurrentSessions?.toString() ?? '',
        isActive: credential.isActive,
      };
    }
    return defaultForm;
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSubmit = async () => {
    if (mode === 'create') {
      if (!form.providerName.trim() || !form.keyName.trim() || !form.apiKey.trim()) {
        toast.error('Provider name, key name, and API key are required');
        return;
      }

      const request: ProviderCredentialCreateRequest = {
        provider_type: form.providerType,
        provider_name: form.providerName.trim(),
        key_name: form.keyName.trim(),
        credentials: { api_key: form.apiKey.trim() },
        usage_type: form.usageType,
        max_concurrent_sessions: form.maxConcurrentSessions
          ? parseInt(form.maxConcurrentSessions, 10)
          : null,
        priority: parseInt(form.priority, 10) || 1,
        is_active: form.isActive,
      };

      try {
        await createMutation.mutateAsync(request);
        toast.success('Credential created');
        onSuccess();
        onClose();
      } catch {
        toast.error('Failed to create credential');
      }
    } else if (credential) {
      const request: ProviderCredentialUpdateRequest = {
        credentials: { api_key: form.apiKey.trim() },
        usage_type: form.usageType,
        key_name: form.keyName.trim() || undefined,
        priority: parseInt(form.priority, 10) || undefined,
        max_concurrent_sessions: form.maxConcurrentSessions
          ? parseInt(form.maxConcurrentSessions, 10)
          : null,
        is_active: form.isActive,
      };

      try {
        await updateMutation.mutateAsync({ id: credential.id, request });
        toast.success('Credential updated');
        onSuccess();
        onClose();
      } catch {
        toast.error('Failed to update credential');
      }
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isEdit = mode === 'edit';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="credential-dialog"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {isEdit ? 'Edit Credential' : 'New Credential'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isPending}
            >
              x
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Provider Type</label>
              {isEdit ? (
                <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600">
                  {form.providerType.toUpperCase()}
                </div>
              ) : (
                <select
                  data-testid="create-provider-type"
                  value={form.providerType}
                  onChange={(e) =>
                    setForm({ ...form, providerType: e.target.value as ProviderType })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {PROVIDER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.toUpperCase()}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Provider Name</label>
              {isEdit ? (
                <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600">
                  {form.providerName}
                </div>
              ) : (
                <input
                  data-testid="create-provider-name"
                  type="text"
                  value={form.providerName}
                  onChange={(e) => setForm({ ...form, providerName: e.target.value })}
                  placeholder="e.g. deepgram, openai"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Key Name</label>
              <input
                data-testid={isEdit ? 'edit-key-name' : 'create-key-name'}
                type="text"
                value={form.keyName}
                onChange={(e) => setForm({ ...form, keyName: e.target.value })}
                placeholder="e.g. key_1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Usage Type</label>
              <select
                data-testid={isEdit ? 'edit-usage-type' : 'create-usage-type'}
                value={form.usageType}
                onChange={(e) => setForm({ ...form, usageType: e.target.value as UsageType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {USAGE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
              <input
                data-testid={isEdit ? 'edit-api-key' : 'create-api-key'}
                type={isEdit ? 'text' : 'password'}
                value={form.apiKey}
                onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                placeholder="API key"
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEdit ? 'font-mono' : ''}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <input
                data-testid={isEdit ? 'edit-priority' : 'create-priority'}
                type="number"
                min="1"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                placeholder="1 = highest"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Concurrent Sessions
              </label>
              <input
                data-testid={isEdit ? 'edit-max-sessions' : 'create-max-sessions'}
                type="number"
                min="1"
                value={form.maxConcurrentSessions}
                onChange={(e) => setForm({ ...form, maxConcurrentSessions: e.target.value })}
                placeholder="Unlimited"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center space-x-2">
                <input
                  data-testid={isEdit ? 'edit-is-active' : 'create-is-active'}
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            data-testid={isEdit ? 'edit-cancel' : 'create-cancel'}
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid={isEdit ? 'edit-save' : 'create-submit'}
            onClick={handleSubmit}
            disabled={isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isPending ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
